import { useMutation } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { lazily } from 'react-lazily';

import {
	postRegister,
	type PostRegisterErrorResponse,
	type PostRegisterRequest,
} from 'services/idm';

import { useCaseInsensitiveParam } from 'hooks/use-case-insensitive-params';
import { useAppTranslation } from 'hooks/use-translates';

import { ApiErrorBlock } from '@login/components/api-error-block';
import { DateOfBirthFields } from '@login/components/fields/date-of-birth-fields';
import { MarketingOptIn } from '@login/components/fields/marketing-opt-in';
import { PasswordFields } from '@login/components/fields/password-fields';
import { FormBox } from '@login/components/form-box';
import { FormNavigation } from '@login/components/form-navigation';
import { MarketingDisclaimer } from '@login/components/marketing-disclaimer';
import { RichText } from '@login/components/rich-text';
import { TurnstileWidget } from '@login/components/turnstile-widget';
import { apiErrorHandler } from '@login/helpers';
import { useApiRedirect } from '@login/hooks/use-api-redirect';
import { useIdmFormErrors } from '@login/hooks/use-idm-form-errors';
import { useScrollToFirstError } from '@login/hooks/use-scroll-to-first-error';
import { useTurnstile } from '@login/hooks/use-turnstile';
import { EMAIL_PATTERN } from '@login/lib/constants';
import {
	REGISTER_ERROR_KEYS,
	REGISTER_FIELD_ORDER,
} from '@login/patterns/form-messages';

import styles from './register.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));
const { Insert } = lazily(() => import('core/generic/elements/form'));
const InfoRequiredFields = lazy(
	() => import('@login/components/info-required-fields/info-required-fields')
);

type RegisterFormUrls = {
	registerUrl?: string;
	signInUrl?: string;
	forgottenPasswordUrl?: string;
	privacyPolicyUrl?: string;
	supportName?: string;
};

type RegisterFormProps = {
	urls: RegisterFormUrls | null;
	minimumAge: number;
	marketingMessage: string;
	disclaimerMessage: string;
	token?: string | null;
	showChallengeForm?: boolean;
};

export const RegisterForm = ({
	urls,
	token,
	minimumAge,
	marketingMessage,
	disclaimerMessage,
	showChallengeForm = false,
}: RegisterFormProps) => {
	const { i18n } = useTranslation();
	const { t } = useAppTranslation(urls);
	const { goToRedirect } = useApiRedirect();
	const returnUrl = useCaseInsensitiveParam('returnUrl');
	const turnstile = useTurnstile(showChallengeForm);

	const apiErrors = useIdmFormErrors({ t, messageKeys: REGISTER_ERROR_KEYS });

	const methods = useForm<PostRegisterRequest>({ mode: 'onBlur' });
	const {
		handleSubmit,
		register,
		formState: { errors },
	} = methods;

	useScrollToFirstError({
		errors,
		apiFieldErrors: apiErrors.fields,
		hasBanner: !!apiErrors.banner,
		fieldOrder: REGISTER_FIELD_ORDER,
	});

	// `mutate`, not `mutateAsync`: failures are handled in onError, and an
	// awaited rejection here would surface as an unhandled promise rejection.
	const { mutate: submitRegister, isPending: isSubmitting } = useMutation({
		mutationKey: ['register', i18n.language, token, returnUrl],
		mutationFn: (formData: PostRegisterRequest) =>
			postRegister({
				data: {
					...formData,
					returnUrl,
					cfToken: turnstile.token,
				},
				lang: i18n.language,
				token,
			}),
		onSuccess: (response) => {
			if (response.action === 'Redirect' && response.redirectUrl) {
				goToRedirect({
					action: response.action,
					url: response.redirectUrl,
				});
				return;
			}
			turnstile.reset();
		},
		onError: (err) => {
			const error = err as unknown as PostRegisterErrorResponse;
			apiErrorHandler(error, i18n.language);
			apiErrors.setFromApiError(error);
			turnstile.reset();
		},
	});

	const handleOnSubmit = useCallback(
		(formData: PostRegisterRequest) => {
			if (turnstile.isPending) {
				apiErrors.setBanner(
					t(
						'common.turnstile_required',
						{},
						'Please complete the security check'
					)
				);
				return;
			}

			apiErrors.clear();
			submitRegister(formData);
		},
		[turnstile.isPending, apiErrors, t, submitRegister]
	);

	const dobLabels = useMemo(
		() => ({
			legend: t('register.dob_label'),
			hint: t('register.dob_help'),
			message: t('register.dob_message'),
			dayPlaceholder: t('register.day_placeholder'),
			monthPlaceholder: t('register.month_placeholder'),
			yearPlaceholder: t('register.year_placeholder'),
			dayRequired: t('register.day_required_error'),
			monthRequired: t('register.month_required_error'),
			yearRequired: t('register.year_required_error'),
			tooYoung: t('register.too_young_error', {
				minimumAge: String(minimumAge),
			}),
		}),
		[t, minimumAge]
	);

	const passwordLabels = useMemo(
		() => ({
			password: t('register.password_label'),
			passwordConfirm: t('register.confirm_password_label'),
			complexityError: t('register.password_complexity_error'),
			nonEnglishError: t('validation.password_english_characters'),
			matchesUsernameError: t('register.password_matches_username_error'),
			mustMatchError: t('register.password_must_match_error'),
		}),
		[t]
	);

	const marketingLabels = useMemo(
		() => ({
			yes: t('register.yes_label'),
			no: t('register.no_label'),
			partnerMessage: t('register.partner_message'),
			optOutMessage: t('register.opt_out_message'),
			transactionalMessage: t('register.transactional_email_message'),
			requiredError: t('register.opt_in_required_error'),
		}),
		[t]
	);

	const bannerMessage = apiErrors.banner || errors.birthDate?.message || '';

	return (
		<Suspense fallback={null}>
			<FormProvider {...methods}>
				<form
					onSubmit={handleSubmit(handleOnSubmit)}
					id="form-profile"
					noValidate
				>
					<FormBox>
						<FormNavigation
							activeBtn="register"
							signInUrl={urls?.signInUrl}
							registerUrl={urls?.registerUrl}
							returnUrl={returnUrl}
						/>
						<div className={styles['form-box-content']}>
							<ApiErrorBlock apiErrorMessage={bannerMessage} />

							<InfoRequiredFields />

							<DateOfBirthFields
								labels={dobLabels}
								minimumAge={minimumAge}
								showRequiredMarker
								resolveError={apiErrors.getFieldError}
							/>

							{!errors.birthDate?.message && (
								<>
									<Insert
										{...register('email', {
											required: t(
												'register.email_required_error'
											),
											pattern: {
												value: EMAIL_PATTERN,
												message: t(
													'register.email_invalid_error'
												),
											},
										})}
										type="email"
										defaultValue={''}
										id="email"
										label={t('register.email_label')}
										required={true}
										error={apiErrors.getFieldError(
											errors.email?.message,
											'Email'
										)}
										errorIcon="AlertCircleFilled"
									/>

									<PasswordFields
										labels={passwordLabels}
										resolveError={apiErrors.getFieldError}
									/>

									<MarketingOptIn
										labels={marketingLabels}
										marketingMessage={marketingMessage}
									/>

									<MarketingDisclaimer
										message={disclaimerMessage}
										privacyPolicyUrl={
											urls?.privacyPolicyUrl
										}
									/>

									{showChallengeForm && (
										<TurnstileWidget
											ref={turnstile.ref}
											action="register"
											language={i18n.language}
											onSuccess={turnstile.onSuccess}
											onExpire={turnstile.onExpire}
											onError={turnstile.onError}
										/>
									)}

									<Button
										type="submit"
										label={t('register.register_button')}
										isLoading={isSubmitting}
										disabled={
											isSubmitting || turnstile.isPending
										}
										variant="primary"
										fullWidth={true}
										className={styles.submitButton}
										dimension="large"
									/>

									<div
										className={`body-small ${styles.signinText}`}
									>
										<RichText
											text={t('common.sigin_link')}
											linkClassName={styles['signin-now']}
										/>
									</div>
								</>
							)}
						</div>
					</FormBox>
				</form>
			</FormProvider>
		</Suspense>
	);
};

export default RegisterForm;
